import React, { useMemo, useState, useRef } from 'react';
import { MessageSquare, Star, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { GOOGLE_REVIEW_URL } from '../config/business';
import { useGoogleReviews } from '../hooks/useGoogleReviews';
import type { GoogleReview, GoogleReviewCategory } from '../services/googleReviewsApi';

/** Fiche Google Maps (liste complète des avis), même place_id que backend/app/api/google-reviews. */
const GOOGLE_REVIEWS_LIST_URL =
  'https://www.google.com/maps/search/?api=1&query=SOS%20IT%20Services&query_place_id=ChIJ493VayLXcS4RVWfNuGIChnI';

const GoogleLogo: React.FC = () => (
  <div className="bg-white px-4 py-2 rounded-md flex items-center gap-2">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M44.5 20H24v8h11.7C34.1 33.1 29.5 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.2-4z"
      />
      <path
        fill="#EA4335"
        d="M6.3 14.7l6.6 4.8C14.5 16 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#FBBC05"
        d="M24 44c5.2 0 9.9-1.8 13.5-4.7l-6.2-5.2C29.5 35.5 26.9 36 24 36c-5.5 0-10.1-2.9-11.7-7.1l-6.6 5.1C9.5 39.6 16.3 44 24 44z"
      />
      <path
        fill="#34A853"
        d="M44.5 20H24v8h11.7c-.8 2.2-2.3 4.1-4.2 5.4l6.2 5.2C41.4 35.1 44.5 30 44.5 24c0-1.3-.1-2.7-.2-4z"
      />
    </svg>
    <span className="text-gray-800 font-bold text-base">Google</span>
  </div>
);

interface StarRatingProps {
  rating: number;
  size?: number;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 16 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((index) => (
      <Star
        key={index}
        size={size}
        fill={index <= Math.round(rating) ? '#FBBC05' : 'transparent'}
        color="#FBBC05"
      />
    ))}
  </div>
);

interface ReviewCardSkeletonProps {
  count: number;
}

const ReviewCardSkeleton: React.FC<ReviewCardSkeletonProps> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex-shrink-0 w-72 bg-surface-2 border border-line rounded-card p-5 animate-pulse"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-white/10" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 bg-white/10 rounded w-3/4" />
            <div className="h-3 bg-white/10 rounded w-1/2" />
          </div>
        </div>
        <div className="flex gap-0.5 mb-3">
          {[...Array(5)].map((_, j) => (
            <div key={j} className="w-4 h-4 bg-white/10 rounded" />
          ))}
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-white/10 rounded w-full" />
          <div className="h-3 bg-white/10 rounded w-5/6" />
          <div className="h-3 bg-white/10 rounded w-4/6" />
        </div>
      </div>
    ))}
  </>
);

interface ReviewCardProps {
  review: GoogleReview;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const initial = review.authorAttribution.displayName.charAt(0).toUpperCase();

  return (
    <div className="w-full bg-surface-2 border border-line rounded-card p-5 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        {review.authorAttribution.photoUri ? (
          <img
            src={review.authorAttribution.photoUri}
            alt={review.authorAttribution.displayName}
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement | null;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className="w-10 h-10 rounded-full bg-accent/15 text-accent-soft font-bold text-sm items-center justify-center flex-shrink-0"
          style={{ display: review.authorAttribution.photoUri ? 'none' : 'flex' }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0">
          <p className="text-fg text-sm font-medium truncate">
            {review.authorAttribution.displayName}
          </p>
          <p className="text-fg-muted text-xs">
            {review.relativePublishTimeDescription}
          </p>
        </div>
      </div>

      <StarRating rating={review.rating} size={14} />

      <p className="mt-3 text-fg-muted text-sm leading-relaxed line-clamp-3 flex-1">
        {review.text.text}
      </p>
    </div>
  );
};

interface GoogleReviewsWidgetProps {
  /** Titre H2 de la section (défaut : appel à laisser un avis). */
  title?: React.ReactNode;
  intro?: string;
  /** Catégorie d'avis à afficher en premier (si au moins un avis correspond). */
  preferredCategory?: GoogleReviewCategory;
  /** Titre utilisé à la place de `title` uniquement si au moins un avis a `preferredCategory`. */
  preferredTitle?: React.ReactNode;
  /** Mobile (< 768px) : 3 avis max + bouton « Voir les N avis ». Désactivé par défaut. */
  collapseOnMobile?: boolean;
}

const MOBILE_REVIEW_LIMIT = 3;

/** Avis de la catégorie préférée d'abord (ordre d'origine conservé), sinon ordre inchangé. */
const sortByPreferredCategory = (
  reviews: GoogleReview[],
  preferredCategory?: GoogleReviewCategory,
): GoogleReview[] => {
  if (!preferredCategory) return reviews;
  const matching = reviews.filter((r) => r.category === preferredCategory);
  if (matching.length > 0) {
    return [...matching, ...reviews.filter((r) => r.category !== preferredCategory)];
  }
  // Aucun avis de la catégorie : les plus récents d'abord.
  const time = (r: GoogleReview): number => (r.publishTime ? Date.parse(r.publishTime) || 0 : 0);
  return [...reviews].sort((a, b) => time(b) - time(a));
};

const DEFAULT_TITLE = (
  <>
    Votre avis nous aide à nous{' '}
    <span className="text-accent-soft">améliorer</span>
  </>
);
const DEFAULT_INTRO =
  "Partagez votre expérience et aidez d'autres personnes à trouver une solution à leurs problèmes informatiques.";

const GoogleReviewsWidget: React.FC<GoogleReviewsWidgetProps> = ({
  title = DEFAULT_TITLE,
  intro = DEFAULT_INTRO,
  preferredCategory,
  preferredTitle,
  collapseOnMobile = false,
}) => {
  const { data, loading, error } = useGoogleReviews();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState<boolean>(false);

  const rawReviews = data?.reviews;
  const reviews = useMemo(
    () => sortByPreferredCategory(rawReviews ?? [], preferredCategory),
    [rawReviews, preferredCategory],
  );
  const totalCount = data?.userRatingCount ?? reviews.length;
  const mobileReviews = expanded ? reviews : reviews.slice(0, MOBILE_REVIEW_LIMIT);
  const canExpand = collapseOnMobile && !expanded && reviews.length > MOBILE_REVIEW_LIMIT;
  const flexDisplay = collapseOnMobile ? 'hidden md:flex' : 'flex';
  const hasReviews = reviews.length > 0;
  const hasPreferred = preferredCategory !== undefined && reviews.some((r) => r.category === preferredCategory);
  const heading = hasPreferred && preferredTitle ? preferredTitle : title;

  const scrollTo = (index: number) => {
    if (!carouselRef.current) return;
    const cards = carouselRef.current.querySelectorAll<HTMLElement>('[data-card]');
    if (cards[index]) {
      cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
    setActiveIndex(index);
  };

  const prev = () => scrollTo(Math.max(0, activeIndex - 1));
  const next = () => scrollTo(Math.min(reviews.length - 1, activeIndex + 1));

  return (
    <section id="avis" className="section-spacing bg-canvas border-t border-line">
      <div className="container-site">
        <div className="card p-4 sm:p-6 md:p-10 relative overflow-hidden">

          {/* Zone haute */}
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="text-center md:text-left max-w-lg">
              <div className="section-subtitle-badge !mb-4">
                <Star size={14} fill="currentColor" />
                <span>Avis Google</span>
              </div>
              <h2 className="heading-section mb-3">{heading}</h2>
              <p className="text-fg-muted leading-[1.6]">{intro}</p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <GoogleLogo />
              {loading ? (
                <div className="flex gap-0.5 animate-pulse">
                  {[...Array(5)].map((_, i) => <div key={i} className="w-5 h-5 bg-white/10 rounded" />)}
                </div>
              ) : data !== null ? (
                <div className="flex flex-col items-center gap-1">
                  {data.rating !== null && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-fg price !font-extrabold text-4xl !tracking-[-0.02em]">{data.rating.toFixed(1).replace('.', ',')}</span>
                      <span className="text-fg-muted price text-sm">/ 5</span>
                    </div>
                  )}
                  <StarRating rating={data.rating ?? 5} size={20} />
                  {data.userRatingCount !== null && (
                    <span className="text-fg-muted text-xs">{data.userRatingCount} avis</span>
                  )}
                </div>
              ) : (
                <StarRating rating={5} size={20} />
              )}
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary !text-sm !px-5"
              >
                <MessageSquare size={16} aria-hidden="true" />
                <span>Laisser un avis Google</span>
                <ExternalLink size={14} className="text-fg-subtle" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Zone basse — Carousel */}
          {error && (
            <p className="relative z-10 text-center text-fg-subtle text-xs py-2">
              Avis temporairement indisponibles
            </p>
          )}

          {!error && (loading || hasReviews) && (
            <div className="relative z-10">
              {/* Carousel */}
              <div
                ref={carouselRef}
                className={`${flexDisplay} gap-4 overflow-x-hidden scroll-smooth items-stretch`}
              >
                {loading ? (
                  <ReviewCardSkeleton count={4} />
                ) : (
                  reviews.map((review, index) => (
                    <div key={index} data-card className="flex-shrink-0 w-72 flex">
                      <ReviewCard review={review} />
                    </div>
                  ))
                )}
              </div>

              {collapseOnMobile && loading && (
                <div className="md:hidden flex flex-col gap-4">
                  <ReviewCardSkeleton count={MOBILE_REVIEW_LIMIT} />
                </div>
              )}

              {collapseOnMobile && !loading && (
                <div className="md:hidden flex flex-col gap-4">
                  {mobileReviews.map((review, index) => (
                    <ReviewCard key={index} review={review} />
                  ))}
                  {/* L'API Places ne renvoie que 5 avis : au-delà, on renvoie vers la fiche Google. */}
                  {collapseOnMobile && totalCount > reviews.length ? (
                    <a
                      href={GOOGLE_REVIEWS_LIST_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary min-h-11 !text-sm self-center"
                    >
                      <span>Voir les {totalCount} avis</span>
                      <ExternalLink size={14} className="text-fg-subtle" aria-hidden="true" />
                    </a>
                  ) : canExpand && (
                    <button
                      type="button"
                      onClick={() => setExpanded(true)}
                      className="btn-secondary min-h-11 !text-sm self-center"
                    >
                      Voir les {totalCount} avis
                    </button>
                  )}
                </div>
              )}

              {/* Contrôles */}
              {!loading && reviews.length > 1 && (
                <div className={`${flexDisplay} items-center justify-center gap-3 sm:gap-4 mt-6`}>
                  <button
                    onClick={prev}
                    disabled={activeIndex === 0}
                    className="w-11 h-11 shrink-0 rounded-full border border-line hover:border-line-strong disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft size={16} className="text-fg" />
                  </button>

                  {/* Dots */}
                  <div className="flex gap-1">
                    {reviews.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => scrollTo(i)}
                        className="group flex h-11 min-w-6 items-center justify-center"
                      >
                        <span
                          className={`block rounded-full transition-all duration-300 ${
                            i === activeIndex
                              ? 'w-6 h-2 bg-accent'
                              : 'w-2 h-2 bg-line-strong group-hover:bg-fg-subtle'
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={next}
                    disabled={activeIndex === reviews.length - 1}
                    className="w-11 h-11 shrink-0 rounded-full border border-line hover:border-line-strong disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    <ChevronRight size={16} className="text-fg" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsWidget;
